import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { DebounceSelect } from 'components/DebounceSelect';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { getLTLCarriersSearch } from 'lib/api/getLTLCarriersSearch';

import {
  QuickQuoteInputs,
  RATED_INTERMODAL_SERVICE_OPTIONS,
  RATED_LTL_SERVICE_OPTIONS,
  RATED_PARCEL_SERVICE_OPTIONS,
  ThreeGCompatibilityProfileItem,
} from '../../types';
import { ProfileCard } from './ProfileCard';
import { useIntegrationId } from './hooks/useIntegrationId';
import { useLTLEquipment } from './hooks/useLTLEquipment';

const CONSTRAINTS_PATH = 'ltl.otherConstraints';

const MODE_OPTIONS = [{ value: '2', label: 'LTL' }];
const SERVICE_OPTION_OPTIONS = [{ value: '1', label: 'LTL' }];
const PARCEL_OPTIONS = RATED_PARCEL_SERVICE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));
const LTL_SERVICE_OPTIONS = RATED_LTL_SERVICE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));
const INTERMODAL_OPTIONS = RATED_INTERMODAL_SERVICE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

interface OtherConstraintsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

export function OtherConstraintsSection({
  formMethods,
}: OtherConstraintsSectionProps) {
  const { control, setValue, watch } = formMethods;
  const [isOpen, setIsOpen] = useState(false);
  const ltl = useWatch({ control, name: 'ltl' });

  const otherConstraints = useWatch({
    control,
    name: 'ltl.otherConstraints',
  });

  const compatibilityProfilesValue = useWatch({
    control,
    name: 'ltl.compatibilityProfiles',
  });

  const compatibilityProfiles = Array.isArray(compatibilityProfilesValue)
    ? compatibilityProfilesValue
    : [];

  const integrationId = useIntegrationId();
  const { options: equipmentOptions, isLoading: isLoadingEquipment } =
    useLTLEquipment(integrationId);

  const { append, remove } = useFieldArray({
    control,
    name: 'ltl.compatibilityProfiles',
  });

  useEffect(() => {
    if (!ltl) {
      return;
    }

    if (otherConstraints == null) {
      setValue('ltl.otherConstraints', {
        mode: '2',
        serviceOption: '1',
      });
    }

    if (!Array.isArray(compatibilityProfilesValue)) {
      setValue('ltl.compatibilityProfiles', []);
    }
  }, [compatibilityProfilesValue, ltl, otherConstraints, setValue]);

  const handleAddProfile = () => {
    const newProfile: ThreeGCompatibilityProfileItem = {
      id: crypto.randomUUID(),
    };
    append(newProfile);
  };

  const fetchCarrierOptions = useCallback(
    async (
      search: string
    ): Promise<Array<{ label: string; value: string }>> => {
      if (!search || search.trim().length === 0) {
        return [];
      }

      const result = await getLTLCarriersSearch(search.trim(), true);

      if (!result.isOk() || !result.value.carriers?.length) {
        return [];
      }

      return result.value.carriers.map((c) => ({
        label: c.tradingPartnerName,
        value: String(c.tradingPartnerId),
      }));
    },
    []
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='w-full'>
      <CollapsibleTrigger type='button' className='w-full cursor-pointer py-2'>
        <Flex align='center' justify='between' className='w-full'>
          <Typography
            variant='h5'
            className='text-neutral-800 font-semibold whitespace-nowrap'
          >
            Are there any other constraints ?
          </Typography>
          {isOpen ? (
            <ChevronUp className='h-4 w-4 text-neutral-500' />
          ) : (
            <ChevronDown className='h-4 w-4 text-neutral-500' />
          )}
        </Flex>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Flex direction='col' gap='lg' className='w-full mx-0 pt-2'>
          <Grid cols='2' gap='lg' className='w-full'>
            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.equipment`}>Equipment</Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.equipment`) ?? '1'}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.equipment` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : '1'
                  )
                }
                options={equipmentOptions}
                showSearch
                placeholder={
                  isLoadingEquipment ? 'Loading...' : 'Select equipment'
                }
                disabled={isLoadingEquipment}
                className='w-full mt-1'
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.carrierTradingPartnerId`}>
                Carrier
              </Label>
              <Controller
                control={formMethods.control}
                name={
                  `${CONSTRAINTS_PATH}.carrierTradingPartnerId` as FieldPath<QuickQuoteInputs>
                }
                render={({ field }) => {
                  const value = field.value as string | undefined;
                  const label = watch(`${CONSTRAINTS_PATH}.carrierLabel`) as
                    string | undefined;
                  return (
                    <DebounceSelect
                      fetchOptions={fetchCarrierOptions}
                      value={
                        value ? { value, label: label ?? value } : undefined
                      }
                      onChange={(option) => {
                        const next = option as
                          { value: string; label: string } | undefined;
                        if (next) {
                          field.onChange(next.value);
                          setValue(
                            `${CONSTRAINTS_PATH}.carrierLabel` as FieldPath<QuickQuoteInputs>,
                            next.label
                          );
                        } else {
                          field.onChange('');
                          setValue(
                            `${CONSTRAINTS_PATH}.carrierLabel` as FieldPath<QuickQuoteInputs>,
                            ''
                          );
                        }
                      }}
                      placeholder='Search carrier'
                      className='w-full mt-1'
                      labelInValue
                    />
                  );
                }}
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.mode`}>Mode</Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.mode`) ?? '2'}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.mode` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : '2'
                  )
                }
                options={MODE_OPTIONS}
                className='w-full mt-1'
                disabled
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.serviceOption`}>
                Service Option
              </Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.serviceOption`) ?? '1'}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.serviceOption` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : '1'
                  )
                }
                options={SERVICE_OPTION_OPTIONS}
                className='w-full mt-1'
                disabled
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.parcelService`}>
                Parcel Service
              </Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.parcelService`) ?? ''}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.parcelService` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : ''
                  )
                }
                options={PARCEL_OPTIONS}
                placeholder='Select parcel service'
                className='w-full mt-1'
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.ltlService`}>LTL Service</Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.ltlService`) ?? ''}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.ltlService` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : ''
                  )
                }
                options={LTL_SERVICE_OPTIONS}
                placeholder='Select LTL service'
                className='w-full mt-1'
              />
            </div>

            <div className='w-full'>
              <Label name={`${CONSTRAINTS_PATH}.intermodalService`}>
                Intermodal Service
              </Label>
              <Select
                value={watch(`${CONSTRAINTS_PATH}.intermodalService`) ?? ''}
                onChange={(value) =>
                  setValue(
                    `${CONSTRAINTS_PATH}.intermodalService` as FieldPath<QuickQuoteInputs>,
                    value != null ? String(value) : ''
                  )
                }
                options={INTERMODAL_OPTIONS}
                placeholder='Select intermodal service'
                className='w-full mt-1'
              />
            </div>
          </Grid>

          <Flex direction='col' gap='md' className='w-full'>
            <Flex align='center' justify='between' className='w-full'>
              <Typography variant='body' weight='medium'>
                Compatibility Profiles
              </Typography>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={handleAddProfile}
                className='h-8 px-3 text-xs'
                buttonNamePosthog={null}
              >
                <Plus className='h-4 w-4 mr-1' />
                Add Profile
              </Button>
            </Flex>

            {compatibilityProfiles.length === 0 && (
              <Flex
                direction='col'
                align='center'
                justify='center'
                className='text-center py-6 text-sm border border-dashed rounded-md w-full border-neutral-300 bg-neutral-50/50 text-neutral-500'
              >
                No compatibility profiles added yet.
              </Flex>
            )}

            <AnimatePresence>
              <Flex direction='col' gap='md' className='w-full'>
                {compatibilityProfiles.map((_, index) => (
                  <ProfileCard
                    key={compatibilityProfiles[index]?.id ?? `profile-${index}`}
                    index={index}
                    formMethods={formMethods}
                    onRemove={() => remove(index)}
                    integrationId={integrationId}
                  />
                ))}
              </Flex>
            </AnimatePresence>
          </Flex>
        </Flex>
      </CollapsibleContent>
    </Collapsible>
  );
}
